import type { FieldValues } from "@mallardbay/lib-react-components"
import queryString from "query-string"
import { omit } from "radash"
import { useLocation } from "@reach/router"
import { navigate } from "gatsby"

import { QUERY_STRING_PROPS } from "~config/constants"

import { navigateToSearchParams } from "./helpers"

const UNDEF_STRING_VALUE = "undefined"

export function getPaymentTypeQueryParam() {
    return getUrlQueryParam(QUERY_STRING_PROPS.PAYMENT_TYPE)
}

export function getLeadIdQueryParam() {
    return getUrlQueryParam(QUERY_STRING_PROPS.LEAD_ID)
}

export function getThreadIdQueryParam() {
    return getUrlQueryParam(QUERY_STRING_PROPS.THREAD_ID)
}

export function useIsFeedbackModeQueryParam() {
    return useBooleanQueryParam(QUERY_STRING_PROPS.IS_FEEDBACK_MODE)
}

export function getGlobalOutfitterIdQueryParam() {
    return getUrlQueryParam(QUERY_STRING_PROPS.GLOBAL_OUTFITTER_ID)
}

export function getGlobalEventOrganizerIdQueryParam() {
    return getUrlQueryParam(QUERY_STRING_PROPS.GLOBAL_EVENT_ORGANIZER_ID)
}

export function getGlobalPortalTypeQueryParam() {
    return getUrlQueryParam(QUERY_STRING_PROPS.GLOBAL_PORTAL_TYPE)
}

export function getAuctionRequestIdQueryParam() {
    return getUrlQueryParam(QUERY_STRING_PROPS.AUCTION_REQUEST_ID)
}

export function getAwardedItemIdQueryParam() {
    return getUrlQueryParam(QUERY_STRING_PROPS.AWARDED_ITEM_ID)
}

export function getPaymentIdQueryParam() {
    return getUrlQueryParam(QUERY_STRING_PROPS.PAYMENT_ID)
}

export function getEditTemplateIdQueryParam() {
    return getUrlQueryParam(QUERY_STRING_PROPS.EDIT_TEMPLATE_ID)
}

export function getFormSubmissionIdQueryParam() {
    return getUrlQueryParam(QUERY_STRING_PROPS.FORM_SUBMISSION_ID)
}

export function getActionQueryParams() {
    const action = getUrlQueryParam(QUERY_STRING_PROPS.ACTION)
    const actionEntityId = getUrlQueryParam(QUERY_STRING_PROPS.ACTION_ENTITY_ID)

    return { action, actionEntityId }
}

function getUrlQueryParam(paramName: string) {
    const paramsObj = getUrlQueryParamsObj()
    const param = paramsObj[paramName]
    const isParamValid =
        typeof param === "string" && param !== UNDEF_STRING_VALUE

    return isParamValid ? param : null
}

function getUrlQueryParamsObj() {
    const search = getUrlQueryParamsString()
    return queryString.parse(search)
}

function getUrlQueryParamsString() {
    if (isServerSide()) {
        return ""
    }
    const { search } = window.location

    return search
}

function isServerSide() {
    return typeof window === "undefined"
}

export function omitEmptyValues(filters: FieldValues) {
    const propsWithEmptyValues = Object.keys(filters).filter(
        (propName) => filters[propName] === ""
    )
    return omit(filters, propsWithEmptyValues)
}

export function clearFilterParamsAndNavigate(
    filterFormKeys: Record<string, string>
) {
    const queryParams = getQueryParamsWithoutFilterParams(filterFormKeys)
    const shouldIgnorePriorParams = true
    navigateToSearchParams(queryParams, shouldIgnorePriorParams)
}

function getQueryParamsWithoutFilterParams(
    filterFormKeys: Record<string, string>
) {
    const queryParams = getUrlQueryParamsObj()
    return omit(queryParams, Object.values(filterFormKeys))
}

function useStringQueryParam(paramName: string) {
    const params = useQueryParams()
    const value = params.get(paramName)

    const setValue = (newValue: string | null) => {
        if (newValue) params.set(paramName, newValue)
        else params.delete(paramName)

        void navigate(`?${params.toString()}`)
    }

    return [value, setValue] as const
}

function useBooleanQueryParam(paramName: string) {
    const [stringValue, setStringValue] = useStringQueryParam(paramName)

    const value = stringValue === "true"
    const setValue = (newValue: boolean) => setStringValue(String(newValue))

    return [value, setValue] as const
}

function useQueryParams() {
    const location = useLocation()

    return new URLSearchParams(location.search)
}
